/**
 * Attribution identifiers of OpenAI Ads, and the one id that ties a browser event to its server twin.
 *
 * Two identifiers travel with a click, and they are NOT interchangeable:
 * - `oppref` — the click reference. It arrives as a query param on the landing URL
 *   (`https://noping.com/trial?oppref=...`) and belongs at the TOP level of a Conversions API event.
 *   The pixel stores it in a first-party `__oppref` cookie, but only once it has loaded, which here is
 *   after `load` + idle. A visitor who signs up before that never had the cookie
 *   written, so we capture the param ourselves on every page load and route change.
 * - `obref` — the browser reference the pixel itself writes to the `__obref` cookie. It belongs inside
 *   `user` on the server event, never at the top level. We only read it; writing it is the pixel's job.
 *
 * The capture runs on the first render, before the pixel mounts: reading a campaign parameter off our
 * own URL into sessionStorage must not wait for `load` + idle, or a fast signup would lose it. There is
 * no consent gate on the pixel or on the conversions (SCRUM-1954, decision of 20/10).
 */

/** Query param OpenAI appends to the landing URL of a click. */
const OPPREF_QUERY_PARAM = 'oppref';

/** Cookie the pixel writes with the same value, read as a fallback when the param is already gone. */
const OPPREF_COOKIE = '__oppref';

/** Cookie the pixel writes with the browser reference. Goes to `user.obref`, never to top-level `oppref`. */
const OBREF_COOKIE = '__obref';

/**
 * sessionStorage, not localStorage: attribution belongs to the visit that came from the ad. A click
 * from three days ago must not claim a trial that someone started from a bookmark today, and the
 * existing UTM capture makes the same call for the same reason.
 */
const OPPREF_STORAGE_KEY = 'noping_openai_oppref';

/** Opaque ids, but still someone else's string in our URL: cap the length and keep it to a safe alphabet. */
const OPAQUE_ID_PATTERN = /^[\w.~-]{1,512}$/;

const isValidOpaqueId = (value: string | null | undefined): value is string => typeof value === 'string' && OPAQUE_ID_PATTERN.test(value);

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`));
  if (!match) return null;

  try {
    return decodeURIComponent(match.slice(name.length + 1));
  } catch {
    return null;
  }
};

const readStoredOppref = (): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    return sessionStorage.getItem(OPPREF_STORAGE_KEY);
  } catch {
    // Private windows and blocked site data throw on access: attribution degrades, the page does not.
    return null;
  }
};

const storeOppref = (value: string): void => {
  try {
    sessionStorage.setItem(OPPREF_STORAGE_KEY, value);
  } catch {
    // Same as above — a visit with no storage still converts, it just cannot be attributed.
  }
};

/**
 * Reads `?oppref=` off the current URL and keeps it for the rest of the visit.
 *
 * Called from the pixel component on every route change because the landing page is not always the
 * page that converts: an ad can land on `/`, and the signup happens two navigations later on `/trial`.
 * The newest valid click wins, the same order `getOppref()` reads in: a second ad clicked in the same tab
 * replaces the first (SCRUM-2559), while a page with no param, or an invalid one, leaves the stored value alone.
 */
export const captureOpprefFromUrl = (): void => {
  if (typeof window === 'undefined') return;

  const fromUrl = new URLSearchParams(window.location.search).get(OPPREF_QUERY_PARAM);
  if (isValidOpaqueId(fromUrl)) storeOppref(fromUrl);
};

/**
 * The click reference for this visit, or `null` when the visitor did not come from an ad.
 *
 * Order matters: the URL wins over what we stored, and both win over the pixel cookie. A fresh param
 * means a fresh click, and the cookie is last because it is the only source that can outlive the visit.
 */
export const getOppref = (): string | null => {
  if (typeof window === 'undefined') return null;

  const fromUrl = new URLSearchParams(window.location.search).get(OPPREF_QUERY_PARAM);
  if (isValidOpaqueId(fromUrl)) return fromUrl;

  const fromStorage = readStoredOppref();
  if (isValidOpaqueId(fromStorage)) return fromStorage;

  const fromCookie = readCookie(OPPREF_COOKIE);
  return isValidOpaqueId(fromCookie) ? fromCookie : null;
};

/**
 * The click reference of THIS visit, for the signup bodies: the URL param, then sessionStorage, and never the
 * `__oppref` cookie. The cookie outlives the visit, so an organic signup days after a click would claim it.
 * `getOppref()` keeps the cookie fallback for the pixel and the Conversions API.
 */
export const getOpprefForSignup = (): string | null => {
  if (typeof window === 'undefined') return null;

  const fromUrl = new URLSearchParams(window.location.search).get(OPPREF_QUERY_PARAM);
  if (isValidOpaqueId(fromUrl)) return fromUrl;

  const fromStorage = readStoredOppref();
  return isValidOpaqueId(fromStorage) ? fromStorage : null;
};

/** The pixel's browser reference, for `user.obref` on the server event. Absent until the pixel loads. */
export const getObref = (): string | null => {
  const fromCookie = readCookie(OBREF_COOKIE);
  return isValidOpaqueId(fromCookie) ? fromCookie : null;
};

/**
 * One id per conversion, reused as the pixel's `event_id` and the Conversions API's `id`.
 *
 * That pairing IS the deduplication: OpenAI matches on (pixel id, event name, event id) and keeps the
 * first arrival. Generating a second id for the server copy would double-count every trial, which is
 * the exact failure this function exists to prevent — so callers must generate once and pass the same
 * value to both transports.
 */
export const createConversionEventId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  // Older Safari and any non-secure context: still unique enough for a dedup key scoped to one pixel.
  return `np-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};
